import React from 'react';

import { getMinLengthError } from '@lender/utils';
import { Divider, Typography } from '@mui/material';
import ListControls from '@sharedCR/common/ListControls';
import { getAddressFieldsConfig } from 'app/pages/loanApplication/LoanApplication.sharedConfigs';
import { NoAvailableMessage } from 'app/shared/components/common/NoAvailableMessage';
import dayjs from 'dayjs';
import _ from 'lodash';
import { compose } from 'redux';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import {
  BaseStatus,
  extendedIncomeTypes,
  IncomeType,
  MAX_PERSON_YEARS,
  TaxFilingType,
} from 'constants/loanApplication';
import {
  baseStatusOptions,
  businessVerificationMethodOptions,
  employedAtTimeOfVerificationOptions,
  employerPhoneSourceOptions,
  incomeTypesOptions,
  probabilityOfContinuedEmploymentOptions,
  salaryOptions,
  taxFilingTypeOptions,
  verificationMethodOptions,
} from 'constants/options';
import { ItemGroupAlignment, ItemLabelPlacement } from 'constants/ui';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { FinancialInfoTestIds } from '../../../../FinancialInfo.testIds';
import { checkboxStyles, hiddenFieldStyles } from '../SectionContent.styles';
import { getPlaceholder } from '../SectionContent.utils';

import ActionButton from './ActionButton';
import {
  EmploymentVerificationTableColumnId,
  titlePerTaxReturnOptions,
} from './EmploymentAndIncome.constants';
import {
  dividerStyles,
  employerAddressTitleStyles,
  emptyMessageCellStyles,
  groupCustomStyles,
  listControlsStyles,
  typographyStyles,
  wrapperWithDividerStyles,
} from './EmploymentAndIncome.styles';
import { getExperienceFieldSchema } from './EmploymentAndIncome.utils';

const BASE_YEAR = 1900;
const EIN_MIN_LENGTH = 9;

export const getSalaryTypeModel = index => `salaryType-${index}`;

const currentDate = Date.now();

export const getConfigFields = ({
  recordId,
  onChangeTaxFillingType,
  ownersOptions,
  isNewRecord,
}) => ({
  owner: {
    fieldId: 'owner',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].owner`,
    inputProps: {
      label: 'Select Borrower',
      placeholder: getPlaceholder(isNewRecord, 'Choose Borrower'),
      disabled: false,
      required: true,
      variant: 'outlined',
      options: ownersOptions,
      'data-test-value': null,
    },
  },
  incomeType: {
    fieldId: 'incomeType',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].incomeType`,
    inputProps: {
      label: 'Income Type',
      placeholder: getPlaceholder(isNewRecord, 'Income Type'),
      disabled: false,
      required: true,
      variant: 'outlined',
      options: incomeTypesOptions,
      'data-test-value': incomeTypesOptions[0].value,
    },
  },
  employerName: {
    fieldId: 'employerName',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].employerName`,
    inputProps: {
      label: 'Employer Name',
      placeholder: getPlaceholder(isNewRecord, 'Employer Name'),
      required: true,
      variant: 'outlined',
      type: FieldInputType.TEXT,
      'data-test-value': 'employer-name',
      'data-test-edited-value': 'employer-name-edited',
    },
  },
  jobTitle: {
    fieldId: 'positionOrTitle',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].positionOrTitle`,
    inputProps: {
      label: 'Job Title',
      placeholder: getPlaceholder(isNewRecord, 'Job Title'),
      required: true,
      variant: 'outlined',
      type: FieldInputType.TEXT,
      'data-test-value': 'employer-name',
      'data-test-edited-value': 'employer-name-edited',
    },
  },
  isCurrentlyEmployedHere: {
    fieldId: 'isCurrentlyEmployedHere',
    type: FieldType.RADIO,
    nameInModel: `[${recordId}].isCurrentlyEmployedHere`,
    inputProps: {
      label: 'Are you currently employed here?',
      options: baseStatusOptions,
      columns: 4,
      required: true,
      align: ItemGroupAlignment.HORIZONTAL,
      labelPlacement: ItemLabelPlacement.END,
      'data-test-value': BaseStatus.YES,
    },
  },
  startDate: {
    fieldId: 'startDate',
    type: FieldType.DATE,
    nameInModel: `[${recordId}].startDate`,
    inputProps: {
      label: 'Start Date',
      disabled: false,
      required: true,
      variant: 'outlined',
      format: COMMON_DATE_FORMAT,
      type: FieldInputType.DATE,
      'data-test-value': dayjs().set('year', BASE_YEAR),
    },
  },
  endDate: {
    fieldId: 'endDate',
    type: FieldType.DATE,
    nameInModel: `[${recordId}].endDate`,
    inputProps: {
      label: 'End Date',
      disabled: false,
      required: true,
      variant: 'outlined',
      format: COMMON_DATE_FORMAT,
      maxDate: dayjs(currentDate).format(COMMON_DATE_FORMAT),
      type: FieldInputType.DATE,
      'data-test-value': dayjs().set('year', BASE_YEAR),
    },
  },
  yearsInProfession: {
    fieldId: 'yearsInProfession',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].yearsInProfession`,
    inputProps: {
      label: 'Years in profession',
      placeholder: getPlaceholder(isNewRecord, 'Years in profession'),
      required: false,
      maxValue: MAX_PERSON_YEARS,
      variant: 'outlined',
      type: FieldInputType.INT,
      'data-test-value': 1,
    },
  },
  monthsInProfession: {
    fieldId: 'monthsInProfession',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].monthsInProfession`,
    inputProps: {
      label: 'Months in profession',
      placeholder: getPlaceholder(isNewRecord, 'Months in profession'),
      required: false,
      maxValue: 11,
      variant: 'outlined',
      type: FieldInputType.INT,
      'data-test-value': 1,
    },
  },
  salary: {
    fieldId: 'salaryType',
    noObserve: true,
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].salary`,
    inputProps: {
      label: 'Salary Type',
      placeholder: getPlaceholder(isNewRecord, 'Base Salary'),
      disabled: false,
      required: true,
      variant: 'outlined',
      options: salaryOptions,
      'data-test-value': salaryOptions[0].value,
    },
  },
  amount: {
    fieldId: 'salaryAmount',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].salary`,
    inputProps: {
      label: 'Amount',
      placeholder: getPlaceholder(isNewRecord, '1.00'),
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.CURRENCY,
      'data-test-value': '$999',
    },
  },
  employmentOwnershipType: {
    fieldId: 'employmentOwnershipType',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].employmentOwnershipType`,
    inputProps: {
      customStyles: checkboxStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      label: 'The borrower has an ownership share of less than 25%',
      columns: 4,
      'data-testid':
        FinancialInfoTestIds.EMPLOYMENT_AND_INCOME_FORM_EMPLOYMENT_OWNERSHIP_TYPE_CHECKBOX,
      'data-test-value': false,
    },
  },
  companyEin: {
    fieldId: 'companyEin',
    type: FieldType.INPUT,
    nameInModel: `[${recordId}].companyEin`,
    inputProps: {
      label: 'Company EIN',
      placeholder: getPlaceholder(isNewRecord, 'Company EIN'),
      required: false,
      format: '##-#######',
      mask: '_',
      allowNegative: false,
      decimalSeparator: false,
      isNumericString: true,
      patternRegExp: new RegExp(/[0-9]{9}/),
      validationMessage: getMinLengthError(EIN_MIN_LENGTH),
      type: FieldInputType.TEXT_SPECIFIC_PATTERN,
      variant: 'outlined',
      dataTestId: FinancialInfoTestIds.EMPLOYMENT_AND_INCOME_FORM_COMPANY_EIN_INPUT,
    },
  },
  taxFilingType: {
    fieldId: 'taxFilingType',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].taxFilingType`,
    inputProps: {
      label: 'Tax Filing Type',
      required: false,
      options: taxFilingTypeOptions,
      customOnChange: onChangeTaxFillingType,
      variant: 'outlined',
      placeholder: getPlaceholder(isNewRecord, 'Tax Filing Type'),
      'data-test-value': TaxFilingType.FORM_1065,
    },
  },
  titlePerTaxReturn: {
    fieldId: 'titlePerTaxReturn',
    type: FieldType.SELECT,
    nameInModel: `[${recordId}].titlePerTaxReturn`,
    inputProps: {
      label: 'Title Per Tax Return',
      required: false,
      options: [],
      variant: 'outlined',
      placeholder: getPlaceholder(isNewRecord, 'Title Per Tax Return'),
    },
  },
  isEmployedByPartyToTransaction: {
    fieldId: 'isEmployedByPartyToTransaction',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].isEmployedByPartyToTransaction`,
    inputProps: {
      customStyles: checkboxStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      label:
        'I am employed by a family member, property seller, real estate agent, or other party to the transaction',
      columns: 4,
      'data-testid':
        FinancialInfoTestIds.EMPLOYMENT_AND_INCOME_FORM_IS_EMPLOYED_BY_PARTY_TO_TRANSACTION_CHECKBOX,
      'data-test-value': false,
    },
  },
  isCorporateRelocation: {
    fieldId: 'isCorporateRelocation',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].isCorporateRelocation`,
    inputProps: {
      customStyles: checkboxStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      label: 'Corporate Relocation',
      'data-testid': FinancialInfoTestIds.EMPLOYMENT_AND_INCOME_FORM_CORPORATE_RELOCATION_CHECKBOX,
      'data-test-value': false,
    },
  },
  isEmployeeLoan: {
    fieldId: 'isEmployeeLoan',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].isEmployeeLoan`,
    inputProps: {
      customStyles: checkboxStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      label: 'Employee Loan',
      'data-testid': FinancialInfoTestIds.EMPLOYMENT_AND_INCOME_FORM_EMPLOYEE_LOAN_CHECKBOX,
      'data-test-value': false,
    },
  },
  foreign: {
    fieldId: 'foreign',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].foreign`,
    inputProps: {
      customStyles: checkboxStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      label: 'Foreign',
      'data-testid': FinancialInfoTestIds.EMPLOYMENT_AND_INCOME_FORM_FOREIGN_CHECKBOX,
      'data-test-value': false,
    },
  },
  seasonal: {
    fieldId: 'seasonal',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].seasonal`,
    inputProps: {
      customStyles: checkboxStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      label: 'Seasonal',
      'data-testid': FinancialInfoTestIds.EMPLOYMENT_AND_INCOME_FORM_SEASONAL_CHECKBOX,
      'data-test-value': false,
    },
  },
  workPhone: {
    fieldId: 'workPhoneNumber',
    type: FieldType.TEL,
    nameInModel: `[${recordId}].workPhoneNumber`,
    inputProps: {
      autoComplete: 'tel-extension',
      label: 'Phone Number',
      placeholder: getPlaceholder(isNewRecord, '000-000-0000'),
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEL_EXT,
      'data-test-value': '731-222-2222',
    },
  },
  isPreviousWorkAddressExist: {
    fieldId: 'isPreviousWorkAddressExist',
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].isPreviousWorkAddressExist`,
    noObserve: true,
    inputProps: {
      customStyles: checkboxStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      columns: 4,
      label: 'I have previous business address',
      'data-testid':
        FinancialInfoTestIds.EMPLOYMENT_AND_INCOME_FORM_IS_PREVIOUS_WORK_ADDRESS_EXIST_CHECKBOX,
      'data-test-value': false,
    },
  },
  isSalaryChanged: {
    noObserve: true,
    type: FieldType.CHECKBOX,
    nameInModel: `[${recordId}].isSalaryChanged`,
    inputProps: {
      customStyles: hiddenFieldStyles,
      iconProps: {
        height: '20',
        width: '20',
      },
      columns: 4,
      className: 'tool__visually-hidden',
      'data-testid': FinancialInfoTestIds.EMPLOYMENT_AND_INCOME_FORM_IS_SALARY_CHANGED_CHECKBOX,
      'data-test-value': false,
    },
  },
});

const formatAddressFields = ({ addressFields, addressModel, onChangeState, isNewRecord }) =>
  Object.entries(addressFields).reduce((acc, [key, field]) => {
    const formattedField = {
      ...field,
      inputProps: {
        ...field.inputProps,
        ...getConditionalObject(key === `${addressModel}.street`, {
          placeholder: 'Street',
        }),
        ...getConditionalObject(key === `${addressModel}.unit`, {
          placeholder: 'Unit',
        }),
        ...getConditionalObject(key === `${addressModel}.city`, {
          placeholder: 'City',
        }),
        ...getConditionalObject(key === `${addressModel}.county`, {
          placeholder: 'County',
        }),
        ...getConditionalObject(key === `${addressModel}.state`, {
          customOnChange: onChangeState,
          placeholder: 'State',
        }),
        ...getConditionalObject(key === `${addressModel}.zip`, {
          label: 'Zip Code',
          placeholder: 'Zip Code',
        }),
        ...getConditionalObject(!isNewRecord, {
          placeholder: '-',
        }),
      },
    };

    acc.push(formattedField);

    return acc;
  }, []);

const getAddressesFields = ({
  recordId,
  isMilitaryAddressAllowed,
  counties,
  workAddressState,
  previousWorkAddressState,
  isPreviousWorkAddressExist,
  onChangeState,
  isNewRecord,
}) => {
  const workAddressModel = `[${recordId}].workAddress`;
  const prevAddressModel = `[${recordId}].previousWorkAddress`;

  const addressFields = getAddressFieldsConfig({
    isStatesWithMilitaryAddresses: isMilitaryAddressAllowed,
    filteredCounties: workAddressState ? counties[workAddressState] : counties.ALL_COUNTIES,
    isWithCountry: false,
    prefix: workAddressModel,
  });

  const previousWorkAddressFields = isPreviousWorkAddressExist
    ? getAddressFieldsConfig({
        isMilitaryAddressAllowed,
        filteredCounties: previousWorkAddressState
          ? counties[previousWorkAddressState]
          : counties.ALL_COUNTIES,
        isWithCountry: false,
        prefix: prevAddressModel,
      })
    : {};

  return {
    workAddressFields: formatAddressFields({
      addressFields,
      addressModel: workAddressModel,
      onChangeState: (e, value) => onChangeState(e, value, 'workAddress'),
      isNewRecord,
    }),
    previousWorkAddressFields: formatAddressFields({
      addressFields: previousWorkAddressFields,
      addressModel: prevAddressModel,
      onChangeState: (e, value) => onChangeState(e, value, 'previousWorkAddress'),
      isNewRecord,
    }),
  };
};

export const getConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    recordId,
    incomeType,
    isCurrentlyEmployedHere,
    previousEmploymentAmountProps,
    visibleSalaryFields,
    onAddSalaryType,
    onRemoveSalaryType,
    onChangeSalaryType,
    onChangeState,
    onChangeTaxFillingType,
    onChangeExperienceField,
    busySalaryTypes,
    isMilitaryAddressAllowed,
    counties,
    isEmploymentOwnershipType,
    taxFilingType,
    isPreviousWorkAddressExist,
    workAddressState,
    previousWorkAddressState,
    isYearsInProfessionRequired,
    isMonthsInProfessionRequired,
    isNewRecord,
    ownersOptions,
    isLoading,
  }) => {
    const employmentAndIncomeConfigFields = getConfigFields({
      recordId,
      onChangeTaxFillingType,
      ownersOptions,
      isNewRecord,
    });

    const isExtendedIncomeType = extendedIncomeTypes.includes(incomeType);
    const visibleSalaryFieldsEntries = Object.entries(visibleSalaryFields);

    const { workAddressFields, previousWorkAddressFields } = getAddressesFields({
      recordId,
      isMilitaryAddressAllowed,
      counties,
      workAddressState,
      previousWorkAddressState,
      isPreviousWorkAddressExist,
      onChangeState,
      isNewRecord,
    });

    return {
      groups: [
        {
          type: FieldType.GROUP,
          fields: [
            ...getConditionalArray(isNewRecord, [employmentAndIncomeConfigFields.owner]),
            employmentAndIncomeConfigFields.incomeType,
          ],
          key: `${recordId}-incomeType`,
        },
        ...getConditionalArray(isExtendedIncomeType, [
          {
            type: FieldType.GROUP,
            fields: [
              employmentAndIncomeConfigFields.employerName,
              employmentAndIncomeConfigFields.jobTitle,
            ],
            key: `${recordId}-employerData`,
          },
          {
            type: FieldType.GROUP,
            fields: [employmentAndIncomeConfigFields.isCurrentlyEmployedHere],
            key: `${recordId}-isCurrentlyEmployedHere`,
          },
        ]),
        {
          type: FieldType.GROUP,
          fields: [
            {
              ...employmentAndIncomeConfigFields.startDate,
              inputProps: {
                ...employmentAndIncomeConfigFields.startDate.inputProps,
                required: isExtendedIncomeType,
              },
            },
            ...getConditionalArray(isExtendedIncomeType, () => [
              ...getConditionalArray(isCurrentlyEmployedHere === false, [
                employmentAndIncomeConfigFields.endDate,
              ]),
              {
                ...employmentAndIncomeConfigFields.yearsInProfession,
                inputProps: {
                  ...employmentAndIncomeConfigFields.yearsInProfession.inputProps,
                  required: isYearsInProfessionRequired,
                  customOnChange: e =>
                    onChangeExperienceField(
                      e,
                      employmentAndIncomeConfigFields.monthsInProfession.nameInModel,
                    ),
                  customSchema: () =>
                    getExperienceFieldSchema(
                      isYearsInProfessionRequired,
                      employmentAndIncomeConfigFields.monthsInProfession.nameInModel,
                    ),
                },
              },
              {
                ...employmentAndIncomeConfigFields.monthsInProfession,
                inputProps: {
                  ...employmentAndIncomeConfigFields.monthsInProfession.inputProps,
                  required: isMonthsInProfessionRequired,
                  customOnChange: e =>
                    onChangeExperienceField(
                      e,
                      employmentAndIncomeConfigFields.yearsInProfession.nameInModel,
                    ),
                  customSchema: () =>
                    getExperienceFieldSchema(
                      isMonthsInProfessionRequired,
                      employmentAndIncomeConfigFields.yearsInProfession.nameInModel,
                    ),
                },
              },
            ]),

            ...getConditionalArray(!isExtendedIncomeType, [
              {
                ...employmentAndIncomeConfigFields.amount,
                nameInModel: `[${recordId}].monthlyIncome`,
                inputProps: {
                  ...employmentAndIncomeConfigFields.amount.inputProps,
                  label: 'Monthly Income',
                  required: true,
                },
              },
            ]),
          ],
          key: `${recordId}-duration`,
        },
        {
          type: FieldType.GROUP,
          fields: [
            ...getConditionalArray(isExtendedIncomeType, () => [
              {
                ...employmentAndIncomeConfigFields.employmentOwnershipType,
                inputProps: {
                  ...employmentAndIncomeConfigFields.employmentOwnershipType.inputProps,
                  ...getConditionalObject(incomeType === IncomeType.SELF_EMPLOYED, {
                    label: 'I have an ownership interest of more than 25% in this employer',
                  }),
                },
              },
              ...getConditionalArray(
                isEmploymentOwnershipType && incomeType === IncomeType.SELF_EMPLOYED,
                [
                  employmentAndIncomeConfigFields.companyEin,
                  employmentAndIncomeConfigFields.taxFilingType,
                  {
                    ...employmentAndIncomeConfigFields.titlePerTaxReturn,
                    inputProps: {
                      ...employmentAndIncomeConfigFields.titlePerTaxReturn.inputProps,
                      options: titlePerTaxReturnOptions[taxFilingType] || [],
                    },
                  },
                ],
              ),
            ]),
          ],
          key: `${recordId}-ownerShipType`,
        },
        ...getConditionalArray(isExtendedIncomeType, () => [
          {
            type: FieldType.GROUP,
            title: {
              wrapperStyles: wrapperWithDividerStyles,
              label: <Divider sx={dividerStyles} />,
            },
            fields: [employmentAndIncomeConfigFields.isSalaryChanged],
            key: `${recordId}-salary-divider`,
          },
          ...getConditionalArray(incomeType === IncomeType.W2, () =>
            visibleSalaryFieldsEntries.map(([salaryTypeModel], index) => {
              const { [salaryTypeModel]: curSalaryType, ...filteredBusySalaryTypes } =
                busySalaryTypes;

              const filteredSalaryOptions = salaryOptions.filter(
                option => !Object.values(filteredBusySalaryTypes).includes(option.value),
              );

              const { onAdd, onRemove } = {
                onAdd: () => onAddSalaryType(index),
                onRemove: () => onRemoveSalaryType(salaryTypeModel),
              };

              const onChange = (e, value) => onChangeSalaryType(e, value, salaryTypeModel);

              return {
                type: FieldType.GROUP,
                customControls: (
                  <ListControls
                    customStyles={listControlsStyles}
                    isAddDisabled={
                      isLoading || visibleSalaryFieldsEntries.length === salaryOptions.length
                    }
                    isRemoveDisabled={isLoading}
                    isRemoveVisible={visibleSalaryFieldsEntries.length > 1}
                    onAdd={onAdd}
                    onRemove={onRemove}
                  />
                ),
                fields: [
                  {
                    ...employmentAndIncomeConfigFields.salary,
                    nameInModel: `[${recordId}].salary.${salaryTypeModel}.input`,
                    inputProps: {
                      ...employmentAndIncomeConfigFields.salary.inputProps,
                      options: filteredSalaryOptions,
                      customOnChange: onChange,
                    },
                  },
                  {
                    ...employmentAndIncomeConfigFields.amount,
                    nameInModel: `[${recordId}].salary.${salaryTypeModel}.value`,
                    inputProps: {
                      ...employmentAndIncomeConfigFields.amount.inputProps,
                      minValue: 0,
                      ...previousEmploymentAmountProps,
                    },
                  },
                ],
                key: `${recordId}-salary-${index}`,
              };
            }),
          ),
          ...getConditionalArray(incomeType === IncomeType.SELF_EMPLOYED, [
            {
              type: FieldType.GROUP,
              fields: [
                {
                  ...employmentAndIncomeConfigFields.amount,
                  nameInModel: `[${recordId}].grossMonthlyBaseSalary`,
                  inputProps: {
                    ...employmentAndIncomeConfigFields.amount.inputProps,
                    label: 'Monthly Compensation',
                    required: true,
                    ...previousEmploymentAmountProps,
                  },
                },
              ],
              key: `${recordId}-self-employed-salary`,
            },
          ]),
          {
            type: FieldType.GROUP,
            fields: [
              ...getConditionalArray(incomeType === IncomeType.W2, [
                employmentAndIncomeConfigFields.isCorporateRelocation,
                employmentAndIncomeConfigFields.isEmployeeLoan,
              ]),
              employmentAndIncomeConfigFields.foreign,
              employmentAndIncomeConfigFields.seasonal,
            ],
            key: `${recordId}-checkboxes`,
          },
          {
            type: FieldType.GROUP,
            title: {
              titleStyles: employerAddressTitleStyles,
              label:
                incomeType === IncomeType.SELF_EMPLOYED ? 'Business Address' : 'Employer Address',
            },
            fields: [
              ...workAddressFields,
              {
                ...employmentAndIncomeConfigFields.workPhone,
                inputProps: {
                  ...employmentAndIncomeConfigFields.workPhone.inputProps,
                  required: incomeType !== IncomeType.SELF_EMPLOYED,
                },
              },
              ...getConditionalArray(
                isEmploymentOwnershipType && incomeType === IncomeType.SELF_EMPLOYED,
                [employmentAndIncomeConfigFields.isPreviousWorkAddressExist],
              ),
            ],
            key: `${recordId}-workAddress`,
            lockAsGroup: true,
          },
          ...getConditionalArray(isPreviousWorkAddressExist, [
            {
              type: FieldType.GROUP,
              title: {
                titleStyles: employerAddressTitleStyles,
                label: 'Previous Business Address',
              },
              fields: [
                ...previousWorkAddressFields,
                {
                  ...employmentAndIncomeConfigFields.workPhone,
                  nameInModel: `[${recordId}].previousWorkPhoneNumber`,
                },
              ],
              key: `${recordId}-previousWorkAddress`,
              lockAsGroup: true,
            },
          ]),
          ...getConditionalArray(incomeType === IncomeType.W2, [
            {
              type: FieldType.GROUP,
              fields: [employmentAndIncomeConfigFields.isEmployedByPartyToTransaction],
              key: `${recordId}-is-employed-by-party-to-transaction`,
            },
          ]),
        ]),
      ],
    };
  },
);

const employmentVerificationFields = {
  employerPhoneNumber: {
    fieldId: 'employerPhoneNumber',
    type: FieldType.TEL,
    nameInModel: 'employerPhoneNumber',
    inputProps: {
      autoComplete: 'tel-extension',
      label: 'Phone Number',
      placeholder: '000-000-0000',
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEL_EXT,
    },
  },
  employerPhoneSource: {
    fieldId: 'employerPhoneSource',
    type: FieldType.SELECT,
    nameInModel: 'employerPhoneSource',
    inputProps: {
      label: 'Employer Phone Source',
      placeholder: 'Employer Phone Source',
      required: false,
      variant: 'outlined',
      options: employerPhoneSourceOptions,
    },
  },
  otherEmployerPhoneSourceDescription: {
    fieldId: 'otherEmployerPhoneSourceDescription',
    type: FieldType.INPUT,
    nameInModel: 'otherEmployerPhoneSourceDescription',
    inputProps: {
      label: 'Other Employer Phone Source Description',
      placeholder: 'Description',
      required: true,
      type: FieldInputType.TEXT,
      variant: 'outlined',
      maxLength: 100,
    },
  },
  employerVerifierName: {
    fieldId: 'employerVerifierName',
    type: FieldType.INPUT,
    nameInModel: 'employerVerifierName',
    inputProps: {
      label: 'Employer Verifier Name',
      placeholder: 'Employer Verifier Name',
      required: false,
      type: FieldInputType.TEXT,
      variant: 'outlined',
      maxLength: 100,
    },
  },
  employerVerifierTitle: {
    fieldId: 'employerVerifierTitle',
    type: FieldType.INPUT,
    nameInModel: 'employerVerifierTitle',
    inputProps: {
      label: 'Employer Verifier Title',
      placeholder: 'Employer Verifier Title',
      required: false,
      type: FieldInputType.TEXT,
      variant: 'outlined',
      maxLength: 100,
    },
  },
  verificationMethod: {
    fieldId: 'verificationMethod',
    type: FieldType.SELECT,
    nameInModel: 'verificationMethod',
    inputProps: {
      label: 'Verification Method',
      placeholder: 'Verification Method',
      required: false,
      variant: 'outlined',
      options: verificationMethodOptions,
    },
  },
  employedAtTimeOfVerification: {
    fieldId: 'employedAtTimeOfVerification',
    type: FieldType.SELECT,
    nameInModel: 'employedAtTimeOfVerification',
    inputProps: {
      label: 'Employed at Time of Verification?',
      placeholder: 'Employed at Time of Verification?',
      required: false,
      variant: 'outlined',
      options: employedAtTimeOfVerificationOptions,
      withSortedOptions: false,
    },
  },
  probabilityOfContinuedEmployment: {
    fieldId: 'probabilityOfContinuedEmployment',
    type: FieldType.SELECT,
    nameInModel: 'probabilityOfContinuedEmployment',
    inputProps: {
      label: 'Probability of Continued Employment?',
      placeholder: 'Probability of Continued Employment?',
      required: false,
      variant: 'outlined',
      options: probabilityOfContinuedEmploymentOptions,
      withSortedOptions: false,
    },
  },
  onTemporaryLeave: {
    fieldId: 'onTemporaryLeave',
    type: FieldType.RADIO,
    nameInModel: 'onTemporaryLeave',
    inputProps: {
      label: 'On Temporary Leave?',
      required: false,
      options: baseStatusOptions,
      variant: 'outlined',
      columns: 4,
    },
  },
  businessExistenceVerified: {
    fieldId: 'businessExistenceVerified',
    type: FieldType.RADIO,
    nameInModel: 'businessExistenceVerified',
    inputProps: {
      label: 'Business Existence Verified',
      required: false,
      options: baseStatusOptions,
      variant: 'outlined',
      columns: 4,
    },
  },
  businessVerificationMethod: {
    fieldId: 'businessVerificationMethod',
    type: FieldType.SELECT,
    nameInModel: 'businessVerificationMethod',
    inputProps: {
      label: 'Business Verification Method',
      placeholder: 'Business Verification Method',
      required: false,
      variant: 'outlined',
      options: businessVerificationMethodOptions,
    },
  },
  position: {
    fieldId: 'position',
    type: FieldType.INPUT,
    nameInModel: 'position',
    inputProps: {
      label: 'Position',
      placeholder: 'Position',
      required: false,
      variant: 'outlined',
      maxLength: 100,
    },
  },
  hireDate: {
    fieldId: 'hireDate',
    type: FieldType.DATE,
    nameInModel: 'hireDate',
    inputProps: {
      label: 'Hire Date',
      required: false,
      includeLocalOffset: true,
      variant: 'outlined',
      format: COMMON_DATE_FORMAT,
      maxDate: dayjs(currentDate).format(COMMON_DATE_FORMAT),
      type: FieldInputType.DATE,
    },
  },
  verifierFullName: {
    fieldId: 'verifierFullName',
    type: FieldType.INPUT,
    nameInModel: 'verifierFullName',
    inputProps: {
      label: 'Verifier Full Name',
      placeholder: 'Verifier Full Name',
      required: false,
      variant: 'outlined',
      maxLength: 100,
    },
  },
  verifierTitle: {
    fieldId: 'verifierTitle',
    type: FieldType.INPUT,
    nameInModel: 'verifierTitle',
    inputProps: {
      label: 'Verifier Title',
      placeholder: 'Verifier Title',
      required: false,
      variant: 'outlined',
      maxLength: 100,
    },
  },
  verifierCompanyName: {
    fieldId: 'verifierCompanyName',
    type: FieldType.INPUT,
    nameInModel: 'verifierCompanyName',
    inputProps: {
      label: 'Verifier Company Name',
      placeholder: 'Verifier Company Name',
      required: false,
      variant: 'outlined',
      maxLength: 100,
    },
  },
  dateCompleted: {
    fieldId: 'dateCompleted',
    type: FieldType.DATE,
    nameInModel: 'dateCompleted',
    inputProps: {
      label: 'Date Completed',
      required: false,
      variant: 'outlined',
      includeLocalOffset: true,
      format: COMMON_DATE_FORMAT,
      maxDate: dayjs(currentDate).format(COMMON_DATE_FORMAT),
      type: FieldInputType.DATE,
    },
  },
  comments: {
    fieldId: 'comments',
    type: FieldType.INPUT,
    nameInModel: 'comments',
    inputProps: {
      label: 'Comments',
      placeholder: 'Comments',
      required: false,
      variant: 'outlined',
      multiline: true,
      rows: 4,
      maxLength: 1000,
      columns: 2,
    },
  },
};

export const getEmploymentVerificationTableConfig = ({
  setEditableEmploymentVerificationData,
  deleteEmploymentVerification,
  editableRowId,
  isEmploymentVerificationActionDisabled,
}) => ({
  [EmploymentVerificationTableColumnId.VERIFICATION_TYPE]: {
    label: 'Type',
    sort: true,
    width: '12%',
    colSpan: ({ verificationType }) => verificationType?.colSpan,
    customStyles: ({ verificationType }) => verificationType?.customStyles,
    content: ({ verificationType }) =>
      verificationType?.content ?? (
        <Typography data-truncated sx={typographyStyles} variant="regular14">
          {verificationType ?? 'Standard'}
        </Typography>
      ),
  },
  [EmploymentVerificationTableColumnId.DATE_COMPLETED]: {
    label: 'Date Completed',
    width: '12%',
    sort: true,
    colSpan: ({ dateCompleted }) => dateCompleted?.colSpan,
    customStyles: ({ dateCompleted }) => dateCompleted?.customStyles,
    content: ({ dateCompleted }) =>
      dateCompleted?.content ?? (
        <Typography data-truncated sx={typographyStyles} variant="regular14">
          {dateCompleted && dayjs(dateCompleted).format(COMMON_DATE_FORMAT)}
        </Typography>
      ),
  },
  [EmploymentVerificationTableColumnId.VERIFIER_FULL_NAME]: {
    label: 'Verifier Full Name',
    sort: true,
    width: '14%',
    content: ({ verifierFullName }) => (
      <Typography data-truncated sx={typographyStyles} variant="regular14">
        {verifierFullName}
      </Typography>
    ),
  },
  [EmploymentVerificationTableColumnId.EMPLOYED_AT_TIME_OF_VERIFICATION]: {
    label: 'Employed at Time of Verification?',
    sort: true,
    width: '19%',
    content: ({ employedAtTimeOfVerification }) => (
      <Typography data-truncated sx={typographyStyles} variant="regular14">
        {employedAtTimeOfVerification}
      </Typography>
    ),
  },
  [EmploymentVerificationTableColumnId.ON_TEMPORARY_LEAVE]: {
    label: 'On Temporary Leave',
    sort: true,
    width: '12%',
    content: ({ onTemporaryLeave }) => {
      const label = onTemporaryLeave ? 'Yes' : 'No';

      return (
        <Typography data-truncated sx={typographyStyles} variant="regular14">
          {_.isNil(onTemporaryLeave) ? '' : label}
        </Typography>
      );
    },
  },
  [EmploymentVerificationTableColumnId.POSITION]: {
    label: 'Position',
    sort: true,
    width: '12%',
    content: ({ position }) => (
      <Typography data-truncated sx={typographyStyles} variant="regular14">
        {position}
      </Typography>
    ),
  },
  [EmploymentVerificationTableColumnId.HIRE_DATE]: {
    label: 'Hire Date',
    sort: true,
    width: '12%',
    content: ({ hireDate }) => (
      <Typography data-truncated sx={typographyStyles} variant="regular14">
        {hireDate && dayjs(hireDate).format(COMMON_DATE_FORMAT)}
      </Typography>
    ),
  },
  action: {
    label: 'Action',
    width: '7%',
    content: rowData => (
      <ActionButton
        deleteEmploymentVerification={deleteEmploymentVerification}
        isDisabled={
          isEmploymentVerificationActionDisabled ||
          (!!editableRowId && rowData.id !== editableRowId)
        }
        isEditDisabled={rowData.id === editableRowId}
        rowData={rowData}
        setEditableEmploymentVerificationData={setEditableEmploymentVerificationData}
      />
    ),
    align: 'center',
  },
});

export const notFoundTableData = [
  {
    [EmploymentVerificationTableColumnId.VERIFICATION_TYPE]: {
      position: 1,
      customStyles: emptyMessageCellStyles,
      colSpan: 8,
      content: <NoAvailableMessage className="empty-message-row" message="No results" />,
    },
  },
];

export const getEmploymentVerificationConfig = compose(
  fillConfigFieldsWithKeys,
  ({ isSelfEmployedIncomeType, isEmployerPhoneSourceOther, isFundingType }) => {
    if (isFundingType) {
      return {
        groups: [
          {
            type: FieldType.GROUP,
            fields: [
              employmentVerificationFields.employedAtTimeOfVerification,
              employmentVerificationFields.verificationMethod,
              employmentVerificationFields.verifierFullName,
              employmentVerificationFields.verifierTitle,
              employmentVerificationFields.verifierCompanyName,
              employmentVerificationFields.dateCompleted,
              employmentVerificationFields.comments,
            ],
            key: `funding-verifier-info`,
          },
        ],
      };
    }

    return {
      groups: [
        {
          type: FieldType.GROUP,
          fields: [
            employmentVerificationFields.employerPhoneNumber,
            employmentVerificationFields.employerPhoneSource,
            ...getConditionalArray(isEmployerPhoneSourceOther, [
              employmentVerificationFields.otherEmployerPhoneSourceDescription,
            ]),
            employmentVerificationFields.employerVerifierName,
            employmentVerificationFields.employerVerifierTitle,
            employmentVerificationFields.verificationMethod,
            employmentVerificationFields.employedAtTimeOfVerification,
            employmentVerificationFields.probabilityOfContinuedEmployment,
            employmentVerificationFields.onTemporaryLeave,
            ...getConditionalArray(isSelfEmployedIncomeType, [
              employmentVerificationFields.businessExistenceVerified,
            ]),
            employmentVerificationFields.businessVerificationMethod,
            employmentVerificationFields.position,
            employmentVerificationFields.hireDate,
          ],
          key: `employer-info`,
        },
        {
          customStyles: groupCustomStyles,
          type: FieldType.GROUP,
          fields: [
            employmentVerificationFields.verifierFullName,
            employmentVerificationFields.verifierTitle,
            employmentVerificationFields.verifierCompanyName,
            employmentVerificationFields.dateCompleted,
            employmentVerificationFields.comments,
          ],
          key: `verifier-info`,
        },
      ],
    };
  },
);
